import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import { initialValues } from "./Schemas/initialValue";
import { validationSchema } from "./Schemas/validation";
import { ModalContext } from "context/ModalContext";
import { addRole } from "Apis/roleApi";
import PropTypes from "prop-types";
import { LoginFormContext } from "context/LoginFormContext";
import { RoleFormContext } from "context/RoleContext";
import { updateRole, getModuleListApi } from "Apis/roleApi";
import { FormGroup, ToggleButton, ToggleButtonGroup, useMediaQuery } from "@mui/material";
import theme from "assets/theme";
import MDSelectBox from "components/MDSelectBox";
import SingleCheckbox from "components/MDCheckBox/MDSingleCheckBox";
import { statusData } from "helpers/constant";
import SubmitButton from "components/SubmitButton";
import colors from "assets/theme/base/colors";
import FormTooltip from "components/MDToolTip";

const AddRoleForm = ({ roleListing }) => {
  const matchDownMd = useMediaQuery(theme.breakpoints.down("sm"));
  const { setNotification } = useContext(LoginFormContext);
  const { editData } = useContext(RoleFormContext);
  const { setOpen, formIsEdit } = useContext(ModalContext);
  const [modules, setModules] = useState([]);
  const [status, setStatus] = useState({
    label: "Inactive",
    value: "0",
  });
  const [isDefault, setIsDefault] = useState(0);
  const { white, info } = colors;

  //setData in Formik...
  const setDataInFormik = async () => {
    formik.setFieldValue("isDefault", editData?.isDefault);
    setIsDefault(editData?.isDefault);
    formik.setValues(editData);
  };

  const getStatusLabel = (id) => {
    const status = statusData.find((item) => item.value == id);
    return status ? status.label : "-";
  };

  const getModules = async (value) => {
    const response = await getModuleListApi();
    if (response?.status === 200) {
      let filteredData = response?.data?.data?.filter(
        (data) => data?.name !== modules?.DASHBOARD && data?.name !== modules?.CALENDAR
      );
      if (value === false) {
        let modules = await filteredData?.map((module) => {
          return {
            ...module,
            permissions: {
              module_id: module?.id,
              read_access: false,
              write_access: false,
              delete_access: false,
            },
          };
        });
        setModules(modules);
      }
      if (value === true) {
        if (editData?.permissions?.length > 0) {
          let modulesData = await editData?.permissions?.map((permission) => {
            let module = permission?.module;
            return {
              ...module,
              permissions: {
                module_id: permission?.module_id,
                read_access: permission?.read_access ? permission?.read_access : false,
                write_access: permission?.write_access ? permission?.write_access : false,
                delete_access: permission?.delete_access ? permission?.delete_access : false,
              },
            };
          });
          setModules(modulesData);
        } else {
          let modules = await filteredData?.map((module) => {
            return {
              ...module,
              permissions: {
                module_id: module?.id,
                read_access: false,
                write_access: false,
                delete_access: false,
              },
            };
          });
          setModules(modules);
        }
      }
    }
  };

  useEffect(() => {
    if (formIsEdit) {
      setDataInFormik();
      getModules(true);
      setStatus({ label: getStatusLabel(editData?.is_active), value: editData?.is_active });
    } else {
      getModules(false);
    }
  }, []);

  //HandleSubmit Form
  const handleSubmit = async (values) => {
    if (!formIsEdit) {
      let response = await addRole(values);
      if (response?.status === 200) {
        roleListing();
        setOpen(false);
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: response.data.message,
          open: true,
        });
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response.data.message,
          open: true,
        });
      }
    } else {
      let editData = await updateRole(values);
      if (editData?.status === 200) {
        roleListing();
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: editData.data.message,
          open: true,
        });
        setOpen(false);
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: editData.data.message,
          open: true,
        });
      }
    }
  };

  const handleStatusChange = (value) => {
    if (value) {
      formik.setFieldValue("is_active", value.value);
      setStatus(value);
    } else {
      formik.setFieldValue("is_active", null);
      setStatus(null);
    }
  };

  //Formik
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  const toggleAccess = (index, key, checkForRead) => {
    let newModules = [...modules];
    let modulePermissions = newModules[index];

    modulePermissions.permissions[key] = !modulePermissions?.permissions[key];

    // Adjust read_access if needed
    if (
      (key === "write_access" || key === "delete_access") &&
      modulePermissions?.permissions[key] &&
      !modulePermissions?.permissions?.read_access
    ) {
      modulePermissions.permissions.read_access = true;
    }

    // Adjust write_access and delete_access if read_access is false
    if (key === "read_access" && !modulePermissions?.permissions[key]) {
      modulePermissions.permissions.write_access = false;
      modulePermissions.permissions.delete_access = false;
    }

    // Adjust read_access if needed when toggling write_access or delete_access
    if (checkForRead && !modulePermissions?.permissions?.read_access)
      modulePermissions.permissions.read_access =
        modulePermissions?.permissions?.write_access ||
        modulePermissions?.permissions?.delete_access;

    setModules(newModules);
    formik.setFieldValue(
      "permissions",
      newModules?.map((module) => module?.permissions)
    );
  };

  const handleIsDefault = (e) => {
    setIsDefault(e?.target?.checked);
    formik.setFieldValue("isDefault", e?.target?.checked ? 1 : 0);
  };

  return (
    <>
      <MDBox component="form" role="form" onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={12} md={6}>
            <MDInput
              type="text"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Name"
              sx={{ width: "100%" }}
              variant="standard"
              required
            />
            {formik.touched.name && formik.errors.name && (
              <MDBox sx={{ fontSize: "12px", color: "red", marginTop: "5px" }}>
                {formik.errors.name}
              </MDBox>
            )}
          </Grid>
          {/* status */}
          <Grid item xs={12} md={6}>
            <MDSelectBox
              name="is_active"
              defaultValue={status}
              options={statusData}
              handleChange={(value) => handleStatusChange(value)}
              label="Status"
              width="100%"
              multiple={false}
              onBlur={formik.handleBlur}
              required
            />
            {formik.touched.is_active && formik.errors.is_active && (
              <MDBox sx={{ fontSize: "12px", color: "red", marginTop: "5px" }}>
                {formik.errors.is_active}
              </MDBox>
            )}
          </Grid>
          <Grid item xs={12} md={6} sx={{ paddingTop: "30px !important" }}>
            <SingleCheckbox
              xs={12}
              sm={12}
              md={12}
              name="isDefault"
              label={
                <>
                  Default role
                  <br />
                  <span style={{ marginTop: "8px", display: "block" }}>
                    (ડિફૉલ્ટ રોલ ડિલીટ કરી શકાય નહીં)
                  </span>
                </>
              }
              onChange={(event) => handleIsDefault(event)}
              onBlur={formik.handleBlur}
              checked={isDefault}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} md={12} sx={{ marginTop: "10px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDBox
                component="h2"
                sx={{ fontSize: "20px", fontWeight: "500", marginBottom: "30px" }}
              >
                Module & Permission
              </MDBox>
            </Grid>
          </Grid>
          <Grid container spacing={2} key={module?.id} sx={{ marginBottom: "10px" }}>
            <Grid item xs={12} sm={4} sx={{ display: `${matchDownMd ? "none" : "block"}` }}>
              <MDBox component="h2" sx={{ fontSize: "18px", fontWeight: "400" }}>
                Module
              </MDBox>
            </Grid>
            <Grid
              item
              xs={12}
              sm={8}
              sx={{
                justifyContent: "flex-end",
                display: `${matchDownMd ? "none" : "flex"}`,
                gap: "13px",
              }}
            >
              <MDBox
                component="div"
                sx={{ minWidth: `${matchDownMd ? "80px" : "98px"}`, opacity: "0" }}
              ></MDBox>
              <MDBox
                variant="subtitle1"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                  padding: "18px 0px !important",
                  height: "0px",
                  minWidth: `${matchDownMd ? "80px" : "98px"}`,
                  fontWeight: "400",
                }}
              >
                Permission
              </MDBox>
              <MDBox
                component="div"
                sx={{ minWidth: `${matchDownMd ? "80px" : "98px"}`, opacity: "0" }}
              ></MDBox>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: `${matchDownMd ? "block" : "none"}`,
                justifyContent: "center",
                borderBottom: "1px solid",
                borderColor: "#c1c1c185",
              }}
            >
              <MDBox variant="h4" gutterBottom>
                Module & Access
              </MDBox>
            </Grid>
          </Grid>
          <FormGroup sx={{ marginTop: "10px" }}>
            {modules?.map((module, index) => (
              <Grid
                container
                spacing={2}
                key={module?.id}
                sx={{ marginY: `${matchDownMd ? "10px" : "0px"}` }}
              >
                <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}>
                  <MDBox variant="h6" gutterBottom sx={{ fontSize: "14px" }}>
                    {module.name}
                  </MDBox>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={8}
                  style={{
                    display: "flex",
                    gap: "13px",
                    justifyContent: "flex-end",
                    justifyContent: `${matchDownMd ? "flex-start" : "flex-end"}`,
                  }}
                >
                  <ToggleButtonGroup
                    value={module?.permissions?.read_access}
                    exclusive
                    onChange={(event, newValue) => toggleAccess(index, "read_access", newValue)}
                    sx={{
                      background: `${module?.permissions?.read_access ? info.main : ""}`,
                    }}
                  >
                    <ToggleButton
                      value={true}
                      sx={{
                        padding: "18px 0px !important",
                        height: "0px",
                        minWidth: `${matchDownMd ? "80px" : "98px"}`,
                        background: `${module?.permissions?.read_access ? info.main : ""}`,
                        fontWeight: 500,
                      }}
                      style={{
                        color: `${module?.permissions?.read_access ? "white" : ""}`,
                        fontWeight: `${module?.permissions?.read_access ? "bold" : ""}`,
                      }}
                      className={module?.permissions?.read_access ? "toggle-button-active" : ""}
                    >
                      Read
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    value={module?.permissions?.write_access}
                    exclusive
                    onChange={(event, newValue) => toggleAccess(index, "write_access", newValue)}
                    sx={{
                      background: `${module?.permissions?.write_access ? info.main : ""}`,
                    }}
                  >
                    <ToggleButton
                      value={true}
                      sx={{
                        padding: "18px 0px !important",
                        height: "0px",
                        minWidth: `${matchDownMd ? "80px" : "98px"}`,
                        background: `${module?.permissions?.write_access ? info.main : ""}`,
                        fontWeight: 500,
                      }}
                      style={{
                        color: `${module?.permissions?.write_access ? "white" : ""}`,
                        fontWeight: `${module?.permissions?.write_access ? "bold" : ""}`,
                      }}
                      className={module?.permissions?.write_access ? "toggle-button-active" : ""}
                    >
                      Write
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    value={module?.permissions?.delete_access}
                    exclusive
                    onChange={(event, newValue) => toggleAccess(index, "delete_access", newValue)}
                    sx={{
                      background: `${module?.permissions?.delete_access ? info.main : ""}`,
                    }}
                  >
                    <ToggleButton
                      value={true}
                      sx={{
                        padding: "18px 0px !important",
                        height: "0px",
                        minWidth: `${matchDownMd ? "80px" : "98px"}`,
                        background: `${module?.permissions?.delete_access ? info.main : ""}`,
                        fontWeight: 500,
                      }}
                      style={{
                        color: `${module?.permissions?.delete_access ? "white" : ""}`,
                        fontWeight: `${module?.permissions?.delete_access ? "bold" : ""}`,
                      }}
                      className={module?.permissions?.delete_access ? "toggle-button-active" : ""}
                    >
                      Delete
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            ))}
          </FormGroup>
        </Grid>
        <MDBox sx={{ width: "100%", textAlign: "center", marginTop: "40px" }}>
          <SubmitButton btnText={formIsEdit ? "Update" : "Add"} type="submit" />
        </MDBox>
      </MDBox>
    </>
  );
};
AddRoleForm.propTypes = {
  roleListing: PropTypes.func,
};
export default AddRoleForm;
